import styled, { ThemeProvider } from "styled-components";
import { MaterialDesignContent, SnackbarProvider } from "notistack";
import { RouterProvider } from "react-router-dom";
import { flushSync } from "react-dom";

import { StateProvider, useStore } from "./store";
import { authRouter } from "./routers/authRouter";
import { appRouter } from "./routers/appRouter";
import { productAccessRouter } from "./routers/productAccessRouter";
import { CheckCircle, IconContext, Info, WarningCircle, XCircle } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { clearLegacyLoggedInUser } from "./services/localStorageServices";
import { Loading } from "./components/Loading";
import { setDefaultOptions } from "date-fns"
import { ptBR } from "date-fns/locale"
import { SocketProvider } from "./providers/socket/socketProvider";
import { exchangeCentralAuthCallback, getAuthenticatedUser, isCentralAuthEnabled, redirectToCentralAuth } from "./services/authServices";
import { buildSessionExpiredLoginUrl } from "./services/authRedirectServices";
import { MotionConfig } from "motion/react";
import { CHAT_PRODUCT_ID, getAssignedRole, hasProductAccess } from "./services/accessControl";

setDefaultOptions({ locale: ptBR });

const autoHideDuration = 3000;

const StyledMaterialDesignContent = styled( MaterialDesignContent )(({ theme }) => ({
  "&.notistack-MuiContent": {
    backgroundColor: theme.colors.background,
    fontWeight: 900,
    padding: theme.spacing.x_small,
    transition: "background .2s",
  },

  "&.notistack-MuiContent > div": {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    gap: theme.spacing.xx_small,
    padding: theme.spacing.x_small,
  },

  "&.notistack-MuiContent-success": {
    color: theme.colors.success.default,
  },

  "&.notistack-MuiContent-info": {
    color: theme.colors.info.default,
  },

  "&.notistack-MuiContent-warning": {
    color: theme.colors.warning.default,
  },

  "&.notistack-MuiContent-error": {
    color: theme.colors.error.default,
  },
}));

function App() {
  return (
    <MotionConfig reducedMotion="user">
      <StateProvider>
        <Content />
      </StateProvider>
    </MotionConfig>
  );
}

function Content() {
  const {
    loading,
    theme,
    user,
    login,
  } = useStore();

  const assignedRole = getAssignedRole( user );
  const productAccessAllowed = hasProductAccess( user, CHAT_PRODUCT_ID );
  const [ userLoading, setUserLoading ] = useState<boolean>( true );
  const [ productAccessUnavailable, setProductAccessUnavailable ] = useState<boolean>( false );
  const router = productAccessUnavailable
    ? productAccessRouter
    : user
    ? productAccessAllowed && assignedRole ? appRouter[ assignedRole ] : productAccessRouter
    : authRouter;
  const routerKey = productAccessUnavailable
    ? "product-access-unavailable"
    : user
    ? productAccessAllowed && assignedRole ? `app-${ assignedRole }` : "product-access"
    : "auth";

  useEffect(() => {
    setUserLoading( true );

    clearLegacyLoggedInUser();

    const loadAuthenticatedUser = async () => {
      if( isCentralAuthEnabled() && window.location.pathname === "/auth/callback" ) {
        const params = new URLSearchParams( window.location.search );
        const response = await exchangeCentralAuthCallback( params.get("code") ?? "", params.get("state") ?? "" );
        if( response.data !== false ) {
          window.location.replace( "/chats" );
          return;
        } else if( response.productAccessUnavailable ) {
          window.history.replaceState( null, "", "/" );
          setProductAccessUnavailable( true );
        } else {
          redirectToCentralAuth();
        }
        setUserLoading( false );
        return;
      }

      const response = await getAuthenticatedUser();

      if( response.data !== false ) {
        const authenticatedUser = response.data;
        setProductAccessUnavailable( false );
        flushSync(() => login( authenticatedUser ));
      } else if( response.productAccessUnavailable ) {
        setProductAccessUnavailable( true );
      } else if( isCentralAuthEnabled()) {
        redirectToCentralAuth();
      } else if( window.location.pathname !== "/" ) {
        window.history.replaceState( null, "", buildSessionExpiredLoginUrl());
      }

      setUserLoading( false );
    };

    void loadAuthenticatedUser();
  }, [ login ]);

  return (
    <ThemeProvider theme={ theme }>
      <IconContext.Provider
        value={{
          size: 24,
          weight: "bold",
        }}
      >
        <SnackbarProvider
          autoHideDuration={ autoHideDuration }
          anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
          maxSnack={ 5 }
          preventDuplicate
          iconVariant={{
            success: <CheckCircle size={24} weight="duotone" />,
            error: <XCircle size={24} weight="duotone" />,
            warning: <WarningCircle size={24} weight="duotone" />,
            info: <Info size={24} weight="duotone" />,
          }}
          Components={{
            success: StyledMaterialDesignContent,
            info: StyledMaterialDesignContent,
            default: StyledMaterialDesignContent,
            error: StyledMaterialDesignContent,
            warning: StyledMaterialDesignContent,
          }}
        >
          {
            userLoading
              ? (
                  <Loading
                    opacity="none"
                  />
                )
              : (
                  productAccessAllowed && !productAccessUnavailable
                    ? (
                        <SocketProvider
                          organizationId={ user?.organization.id ?? ""}
                          userId={ user?.id ?? ""}
                        >
                          <RouterProvider key={ routerKey } router={ router } />
                        </SocketProvider>
                      )
                    : <RouterProvider key={ routerKey } router={ router } />
                )
          }

          {
            loading && <Loading />
          }
        </SnackbarProvider>
      </IconContext.Provider>
    </ThemeProvider>
  );
}

export default App;
